import { type ApiLinks, getLink } from '@/api/links';
import type { Url } from '@/domain/types';
import type { LinkMap } from '@/api/convertLinks';

/**
 * A more consise version of a linked-representation type that:
 *
 * 1. Has had the links in Map converted into properties
 * 2. Has had the existing properties in Props changed from snake_case to camelCase
 */
export type ConvertedRepresentation<Props, Map extends LinkMap> = { [Name in keyof Map]: Url } & {
    [K in keyof Props as _ToCamelCase<K>]: Props[K];
};

/**
 * Convert a linked-representation into a more consise form, with:
 *
 * 1. Links in Map converted into properties
 * 2. Existing properties in Props renamed from snake_case to camelCase
 */
export function convertRepresentation<Repr extends ApiLinks, Map extends LinkMap>(
    repr: Repr,
    linkMap: Map,
): ConvertedRepresentation<Repr, Map> {
    return {
        ...(Object.fromEntries([
            ...Object.entries(linkMap).map(([name, selector]) => [name, getLink(repr, selector)]),
            ...Object.entries(repr).map(([name, value]) => [_toCamelCase(name), value]),
        ]) as ConvertedRepresentation<Repr, Map>),
    };
}

/** The type of string S converted from snake-case to camel-case */
type _ToCamelCase<S> = S extends `${infer T}_${infer U}`
    ? `${T}${Capitalize<_ToCamelCase<U>>}`
    : S;

/** Convert the given string from snake-case to camel-case */
export function _toCamelCase<S extends string>(value: S): _ToCamelCase<S> {
    return value
        .toLowerCase()
        .replace(/(_[a-z0-9])/g, (word) => word.slice(-1).toUpperCase()) as _ToCamelCase<S>;
}
